'use client';

import { useEffect } from 'react';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';
import { Link } from '@/core/i18n/navigation';
import { cn } from '@/shared/lib/utils';
import { trackPromoBannerView, trackPromoBannerClick } from '@/shared/lib/analytics';

interface PromoBannerProps {
  className?: string;
}

export function InlinePromoBanner({ className }: PromoBannerProps) {
  // Track banner view on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPromoBannerView({
        page_path: window.location.pathname,
        banner_type: 'inline_discount',
      });
    }
  }, []);

  // Handle banner click
  const handleBannerClick = () => {
    if (typeof window !== 'undefined') {
      trackPromoBannerClick({
        page_path: window.location.pathname,
        banner_type: 'inline_discount',
        destination: '/pricing',
      });
    }
  };

  return (
    <section className={cn('py-3', className)}>
      <div className="container">
        <Link
          href="/pricing"
          onClick={handleBannerClick}
          className="group relative block overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-1 transition-all hover:shadow-2xl hover:shadow-orange-500/50"
        >
          <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 px-6 py-4 md:px-8 md:py-5">
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />

            <div className="relative flex flex-col items-center justify-center gap-3 text-center md:flex-row md:justify-between md:text-left">
              {/* Left side - Main message */}
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Zap className="h-6 w-6 text-white" fill="currentColor" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
                      🔥 LIMITED TIME
                    </span>
                    <span className="text-2xl font-black text-white md:text-3xl">
                      37% OFF
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-white/90 md:text-base">
                    Unlock unlimited AI transformations · Save big today!
                  </p>
                </div>
              </div>

              {/* Right side - CTA */}
              <div className="flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-bold text-orange-600 shadow-lg transition-all group-hover:scale-105 group-hover:shadow-xl">
                <Sparkles className="h-5 w-5" />
                <span>Claim Discount</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
